import { useEffect, useState } from 'preact/hooks';
import {
  ADMIN_LIST_NAMES,
  listDef,
  type AdminListName,
  type AdminRow,
  type FieldDef,
} from '../../shared/admin/lists';
import { useI18n, type MessageKey } from '../i18n';
import { ApiError, apiGet, apiSend } from '../lib/api';

// Admin screen for the reference lists (P1.1). The form is drawn from the shared list
// definitions; the broker validates again and audits every change. UI hiding is cosmetic only.

interface ListResponse {
  list: AdminListName;
  canCreate: boolean;
  rows: AdminRow[];
}

type Load =
  | { kind: 'loading' }
  | { kind: 'ok'; data: ListResponse }
  | { kind: 'error'; code: string; reference: string | null };

type Draft = Record<string, string | boolean>;
type Editing = { mode: 'new' } | { mode: 'edit'; row: AdminRow } | null;

const ERROR_KEYS = new Set([
  'invalid_input',
  'duplicate',
  'conflict',
  'forbidden',
  'not_allowed',
  'data_unavailable',
  'not_configured',
]);

function errorKey(code: string): MessageKey {
  return (ERROR_KEYS.has(code) ? `admin.error.${code}` : 'admin.error.generic') as MessageKey;
}

function toDraft(fields: readonly FieldDef[], row?: AdminRow): Draft {
  const draft: Draft = {};
  for (const f of fields) {
    const v = row?.fields[f.name];
    draft[f.name] =
      f.kind === 'bool'
        ? row
          ? v === true
          : true
        : v === undefined || v === null
          ? ''
          : String(v);
  }
  return draft;
}

function toValue(f: FieldDef, v: string | boolean): unknown {
  if (f.kind === 'bool') return v === true;
  if (f.kind === 'number') return v === '' ? undefined : Number(v);
  return typeof v === 'string' ? v.trim() : v;
}

/** New rows send every filled field; edits send only what changed. */
function payload(
  fields: readonly FieldDef[],
  draft: Draft,
  original?: Draft,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    if (original && (f.key || draft[f.name] === original[f.name])) continue;
    const value = toValue(f, draft[f.name] ?? '');
    if (!original && (value === '' || value === undefined)) continue;
    if (value !== undefined) out[f.name] = value;
  }
  return out;
}

function display(f: FieldDef, v: unknown, t: (k: MessageKey) => string): string {
  if (f.kind === 'bool') return v === true ? t('admin.yes') : t('admin.no');
  if (f.kind === 'enum' && f.name === 'Type' && typeof v === 'string') {
    return t(`admin.type.${v}` as MessageKey);
  }
  return v === undefined || v === null ? '' : String(v);
}

function FieldInput(props: {
  f: FieldDef;
  value: string | boolean;
  readOnly: boolean;
  error: string | undefined;
  onChange: (v: string | boolean) => void;
}) {
  const { t } = useI18n();
  const { f, value, readOnly, error, onChange } = props;
  const id = `admin-field-${f.name}`;
  const label = t(`admin.field.${f.name}` as MessageKey);
  const describedBy = error ? `${id}-error` : undefined;
  const common = {
    id,
    name: f.name,
    'aria-invalid': error ? (true as const) : undefined,
    'aria-describedby': describedBy,
    disabled: readOnly,
  };

  let control;
  if (f.kind === 'bool') {
    return (
      <div class="field field--check">
        <input
          type="checkbox"
          {...common}
          checked={value === true}
          onChange={(e) => onChange((e.target as HTMLInputElement).checked)}
        />
        <label for={id}>{label}</label>
      </div>
    );
  } else if (f.kind === 'enum') {
    control = (
      <select
        {...common}
        value={String(value)}
        onChange={(e) => onChange((e.target as HTMLSelectElement).value)}
      >
        <option value="">—</option>
        {(f.options ?? []).map((o) => (
          <option key={o} value={o}>
            {f.name === 'Type' ? t(`admin.type.${o}` as MessageKey) : o}
          </option>
        ))}
      </select>
    );
  } else if (f.kind === 'note') {
    control = (
      <textarea
        {...common}
        rows={3}
        maxLength={f.max}
        value={String(value)}
        onInput={(e) => onChange((e.target as HTMLTextAreaElement).value)}
      />
    );
  } else {
    control = (
      <input
        {...common}
        type={f.kind === 'email' ? 'email' : f.kind === 'number' ? 'number' : 'text'}
        dir={f.kind === 'email' || f.kind === 'number' ? 'ltr' : undefined}
        inputMode={f.kind === 'number' ? 'decimal' : undefined}
        maxLength={f.max}
        required={f.required}
        value={String(value)}
        onInput={(e) => onChange((e.target as HTMLInputElement).value)}
      />
    );
  }
  return (
    <div class="field">
      <label for={id}>
        {label}
        {f.required && <span aria-hidden="true"> *</span>}
      </label>
      {control}
      {error && (
        <p id={`${id}-error`} class="field__error" dir="auto">
          {error}
        </p>
      )}
    </div>
  );
}

function RowForm(props: {
  list: AdminListName;
  editing: Exclude<Editing, null>;
  onDone: (saved: boolean) => void;
}) {
  const { t } = useI18n();
  const { list, editing, onDone } = props;
  const fields = listDef(list).fields;
  const original = editing.mode === 'edit' ? toDraft(fields, editing.row) : undefined;
  const [draft, setDraft] = useState<Draft>(() => original ?? toDraft(fields));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<MessageKey | null>(null);
  const [saving, setSaving] = useState(false);

  async function save(e: Event) {
    e.preventDefault();
    const body = payload(fields, draft, original);
    if (Object.keys(body).length === 0) return onDone(false);
    setSaving(true);
    setErrors({});
    setProblem(null);
    try {
      if (editing.mode === 'new') await apiSend('POST', `/api/admin/${list}`, { fields: body });
      else
        await apiSend('PATCH', `/api/admin/${list}`, {
          id: editing.row.id,
          etag: editing.row.etag,
          fields: body,
        });
      onDone(true);
    } catch (err) {
      const apiErr = err instanceof ApiError ? err : null;
      setProblem(errorKey(apiErr?.code ?? 'generic'));
      const fieldErrors: Record<string, string> = {};
      for (const [k, v] of Object.entries(apiErr?.fields ?? {}))
        fieldErrors[k.replace(/^fields\./, '')] = v;
      setErrors(fieldErrors);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form class="card admin-form" onSubmit={(e) => void save(e)} noValidate>
      <h2 class="card__title">{editing.mode === 'new' ? t('admin.add') : t('admin.edit')}</h2>
      {problem && (
        <p class="form-problem" role="alert">
          {t(problem)}
          {errors._ && <span dir="auto"> {errors._}</span>}
        </p>
      )}
      <div class="admin-form__grid">
        {fields.map((f) => (
          <FieldInput
            key={f.name}
            f={f}
            value={draft[f.name] ?? ''}
            readOnly={editing.mode === 'edit' && f.key === true}
            error={errors[f.name]}
            onChange={(v) => setDraft({ ...draft, [f.name]: v })}
          />
        ))}
      </div>
      <div class="actions">
        <button type="submit" class="button button--primary" disabled={saving}>
          {saving ? t('admin.saving') : t('admin.save')}
        </button>
        <button type="button" class="button" onClick={() => onDone(false)}>
          {t('admin.cancel')}
        </button>
      </div>
    </form>
  );
}

export function AdminPage() {
  const { t } = useI18n();
  const [list, setList] = useState<AdminListName>('VerticalEntityMap');
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [editing, setEditing] = useState<Editing>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let live = true;
    setLoad({ kind: 'loading' });
    apiGet<ListResponse>(`/api/admin/${list}`)
      .then((data) => live && setLoad({ kind: 'ok', data }))
      .catch((err: unknown) => {
        if (!live) return;
        const e = err instanceof ApiError ? err : null;
        setLoad({ kind: 'error', code: e?.code ?? 'generic', reference: e?.correlationId ?? null });
      });
    return () => {
      live = false;
    };
  }, [list, reloadKey]);

  const fields = listDef(list).fields;

  return (
    <div class="stack">
      <div class="admin-tabs" role="group" aria-label={t('admin.lists')}>
        {ADMIN_LIST_NAMES.map((name) => (
          <button
            key={name}
            type="button"
            class="chip"
            aria-pressed={name === list}
            onClick={() => {
              setList(name);
              setEditing(null);
              setSaved(false);
            }}
          >
            {t(`admin.list.${name}` as MessageKey)}
          </button>
        ))}
      </div>

      {saved && (
        <p class="form-saved" role="status">
          {t('admin.saved')}
        </p>
      )}

      {editing && (
        <RowForm
          key={`${list}-${editing.mode === 'edit' ? editing.row.id : 'new'}`}
          list={list}
          editing={editing}
          onDone={(didSave) => {
            setEditing(null);
            setSaved(didSave);
            if (didSave) setReloadKey(reloadKey + 1);
          }}
        />
      )}

      <section class="card" aria-labelledby="admin-list-title">
        <div class="card__head">
          <h2 id="admin-list-title" class="card__title">
            {t(`admin.list.${list}` as MessageKey)}
          </h2>
          {load.kind === 'ok' && load.data.canCreate && !editing && (
            <button type="button" class="button" onClick={() => setEditing({ mode: 'new' })}>
              {t('admin.add')}
            </button>
          )}
        </div>
        {load.kind === 'loading' && <p aria-busy="true">{t('admin.loading')}</p>}
        {load.kind === 'error' && (
          <div role="alert">
            <p>{t(errorKey(load.code))}</p>
            {load.reference && (
              <p class="caption" dir="ltr">
                {t('error.reference', { ref: load.reference })}
              </p>
            )}
          </div>
        )}
        {load.kind === 'ok' &&
          (load.data.rows.length === 0 ? (
            <p>{t('admin.empty')}</p>
          ) : (
            <div class="table-wrap">
              <table class="table">
                <thead>
                  <tr>
                    {fields.map((f) => (
                      <th key={f.name} scope="col">
                        {t(`admin.field.${f.name}` as MessageKey)}
                      </th>
                    ))}
                    <th scope="col">
                      <span class="visually-hidden">{t('admin.actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {load.data.rows.map((row) => (
                    <tr key={row.id}>
                      {fields.map((f) => (
                        <td key={f.name} dir={f.kind === 'email' ? 'ltr' : 'auto'}>
                          {display(f, row.fields[f.name], t)}
                        </td>
                      ))}
                      <td>
                        <button
                          type="button"
                          class="button button--small"
                          aria-label={t('admin.editRow', {
                            name: String(row.fields[fields[0]?.name ?? ''] ?? ''),
                          })}
                          onClick={() => {
                            setSaved(false);
                            setEditing({ mode: 'edit', row });
                          }}
                        >
                          {t('admin.edit')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
      </section>
      <JobsCard />
      <NightlyCard />
    </div>
  );
}

type JobName = 'supplierExpiry' | 'rfqReminders' | 'cfoReminders' | 'contractAlerts';

/** Runs a scheduled job now (the same code as the cron Worker), e.g. to test on the test site. */
function JobsCard() {
  const { t } = useI18n();
  const [busy, setBusy] = useState<JobName | null>(null);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  async function run(job: JobName) {
    setBusy(job);
    setResult(null);
    try {
      const out = await apiSend<{ sent: number; mailConfigured: boolean }>(
        'POST',
        '/api/jobs/run',
        {
          job,
        },
      );
      setResult({
        ok: out.mailConfigured,
        text: out.mailConfigured
          ? t('admin.jobs.done', { n: String(out.sent) })
          : t('admin.jobs.noMail'),
      });
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      setResult({ ok: false, text: t(errorKey(e?.code ?? 'generic')) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <section class="card stack" aria-labelledby="admin-jobs">
      <h2 id="admin-jobs" class="card__title">
        {t('admin.jobs.title')}
      </h2>
      <p class="caption">{t('admin.jobs.hint')}</p>
      <div class="actions">
        {(['supplierExpiry', 'rfqReminders', 'cfoReminders', 'contractAlerts'] as const).map(
          (job) => (
            <button
              key={job}
              type="button"
              class="button"
              disabled={busy !== null}
              onClick={() => void run(job)}
            >
              {t(`admin.jobs.${job}` as MessageKey)}
            </button>
          ),
        )}
      </div>
      {result && (
        <p class={result.ok ? 'form-saved' : 'form-problem'} role={result.ok ? 'status' : 'alert'}>
          {result.text}
        </p>
      )}
    </section>
  );
}

interface NightlyOut {
  result:
    | { year: number; months: number; created: number; updated: number; unchanged: number }
    | { problems: string[]; notes: string[]; mailed: boolean };
}

/** The nightly jobs (P5.1): the dashboard summary for a year, and the health check. */
function NightlyCard() {
  const { t } = useI18n();
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(String(thisYear));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; lines: string[] } | null>(null);

  async function run(job: 'insights' | 'health') {
    setBusy(true);
    setResult(null);
    try {
      const body = job === 'insights' ? { job, year: Number(year) } : { job };
      const out = await apiSend<NightlyOut>('POST', '/api/jobs/run', body);
      const r = out.result;
      setResult(
        'problems' in r
          ? {
              ok: r.problems.length === 0,
              lines: [
                r.problems.length
                  ? t('admin.nightly.problems', { n: String(r.problems.length) })
                  : t('admin.nightly.healthy'),
                ...r.problems,
                ...r.notes,
              ],
            }
          : {
              ok: true,
              lines: [
                t('admin.nightly.built', {
                  year: String(r.year),
                  rows: String(r.months),
                  created: String(r.created),
                  updated: String(r.updated),
                }),
              ],
            },
      );
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      setResult({ ok: false, lines: [t(errorKey(e?.code ?? 'generic'))] });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section class="card stack" aria-labelledby="admin-nightly">
      <h2 id="admin-nightly" class="card__title">
        {t('admin.nightly.title')}
      </h2>
      <p class="caption">{t('admin.nightly.hint')}</p>
      <div class="actions">
        <div class="field admin-year">
          <label class="visually-hidden" for="admin-year">
            {t('admin.nightly.year')}
          </label>
          <select
            id="admin-year"
            value={year}
            onChange={(e) => setYear((e.target as HTMLSelectElement).value)}
          >
            {[0, 1, 2, 3, 4].map((d) => (
              <option key={d} value={String(thisYear - d)}>
                {thisYear - d}
              </option>
            ))}
          </select>
        </div>
        <button type="button" class="button" disabled={busy} onClick={() => void run('insights')}>
          {t('admin.nightly.insights')}
        </button>
        <button type="button" class="button" disabled={busy} onClick={() => void run('health')}>
          {t('admin.nightly.health')}
        </button>
      </div>
      {result && (
        <div
          class={result.ok ? 'form-saved' : 'form-problem'}
          role={result.ok ? 'status' : 'alert'}
        >
          {result.lines.map((l, i) => (
            <p key={i}>{l}</p>
          ))}
        </div>
      )}
    </section>
  );
}
